import {ApplicationType} from 'ApplicationEntry/models/ApplicationType';
import * as G from 'ApplicationEntry/models/ProductsRiders/HeartStroke/GroupHeartStroke';
import {HeartStroke, matchHeartStrokeByType} from 'ApplicationEntry/models/ProductsRiders/HeartStroke/HeartStroke';
import * as NY from 'ApplicationEntry/models/ProductsRiders/HeartStroke/NewYorkHeartStroke';
import * as O from 'ApplicationEntry/models/ProductsRiders/HeartStroke/OrdinaryHeartStroke';
import {Plan} from 'ApplicationEntry/models/ProductsRiders/PlanModel';
import {Async, Loading} from 'Core/models/Async';
import {NonEmptyString} from 'Core/models/EmptyString';
import {createAction} from 'Shared/utilities/actionCreator';
import {reverseEnum} from 'Shared/utilities/enumUtility';

export enum HeartStrokeActionType {
  CHANGE_PRODUCT_TYPE = 'heartStroke/CHANGE_PRODUCT_TYPE',
  CHANGE_PLAN_TYPE = 'heartStroke/CHANGE_PLAN_TYPE',
  CHANGE_PREMIUM = 'heartStroke/CHANGE_PREMIUM',
  CHANGE_CALCULATED_PREMIUM = 'heartStroke/CHANGE_CALCULATED_PREMIUM',
  CHANGE_CWA = 'heartStroke/CHANGE_CWA',
  CHANGE_SECTION125 = 'heartStroke/CHANGE_SECTION125',
  CHANGE_REPLACEMENT = 'heartStroke/CHANGE_REPLACEMENT',
  CHANGE_POLICY_NUMBER = 'heartStroke/CHANGE_POLICY_NUMBER',
  CHANGE_POLICY_OPTION = 'heartStroke/CHANGE_POLICY_OPTION',
  CHANGE_RIDER_CODES = 'heartStroke/CHANGE_RIDER_CODES',
  CHANGE_RIDER_AMT = 'heartStroke/CHANGE_RIDER_AMT',
  CHANGE_OVERRIDE_BILL_FORM = 'heartStroke/CHANGE_OVERRIDE_BILL_FORM',
  CHANGE_BASE_UNITS = 'heartStroke/CHANGE_BASE_UNITS',
  GET_HEART_STROKE_PRODUCTS = 'heartStroke/GET_HEART_STROKE_PRODUCTS',
  CHANGE_HEART_STROKE_PRODUCTS = 'heartStroke/CHANGE_HEART_STROKE_PRODUCTS',
  CHANGE_RIDER_INFORMATION = 'heartStroke/CHANGE_RIDER_INFORMATION',
  CLEAR_HEART_STROKE = 'heartStroke/CLEAR_HEART_STROKE',
}

export const isHeartStrokeAction = (action: any): action is HeartStrokeAction =>
  action.type in reverseEnum(HeartStrokeActionType);

export const changeProductType = (productType: string) =>
  createAction(HeartStrokeActionType.CHANGE_PRODUCT_TYPE, {productType});
export const changePlanType = (planType: string) => createAction(HeartStrokeActionType.CHANGE_PLAN_TYPE, {planType});
export const changePolicyOption = (policyOption: string) =>
  createAction(HeartStrokeActionType.CHANGE_POLICY_OPTION, {policyOption});
export const changePremium = (premium: string) => createAction(HeartStrokeActionType.CHANGE_PREMIUM, {premium});
export const changeCalculatedPremium = (calculatedPremium: string) =>
  createAction(HeartStrokeActionType.CHANGE_CALCULATED_PREMIUM, {calculatedPremium});
export const changeCWA = (cwa: string) => createAction(HeartStrokeActionType.CHANGE_CWA, {cwa});
export const changeSection125 = (section125: boolean) =>
  createAction(HeartStrokeActionType.CHANGE_SECTION125, {section125});
export const changeReplacement = (replacement: boolean) =>
  createAction(HeartStrokeActionType.CHANGE_REPLACEMENT, {replacement});
export const changePolicyNumber = (policyNumber: string) =>
  createAction(HeartStrokeActionType.CHANGE_POLICY_NUMBER, {policyNumber});
export const changeRiderCodes = (riderCode: string, index: number) =>
  createAction(HeartStrokeActionType.CHANGE_RIDER_CODES, {riderCode, index});
export const changeRiderAmt = (riderAmt: number, index: number) =>
  createAction(HeartStrokeActionType.CHANGE_RIDER_AMT, {riderAmt, index});
export const changeOverrideBillForm = (overrideBillForm: boolean) =>
  createAction(HeartStrokeActionType.CHANGE_OVERRIDE_BILL_FORM, {overrideBillForm});
export const getHeartStrokeProducts = (applicationType: ApplicationType, family: string) =>
  createAction(HeartStrokeActionType.GET_HEART_STROKE_PRODUCTS, {applicationType, family});
export const changeHeartStrokeProducts = (products: Async<Plan[]>) =>
  createAction(HeartStrokeActionType.CHANGE_HEART_STROKE_PRODUCTS, {products});
export const changeBaseUnits = (baseUnits: string) =>
  createAction(HeartStrokeActionType.CHANGE_BASE_UNITS, {baseUnits});
export const changeRiderInformation = (amount: number, code: string, index: number) =>
  createAction(HeartStrokeActionType.CHANGE_RIDER_INFORMATION, {amount, code, index});
export const clearHeartStroke = () => createAction(HeartStrokeActionType.CLEAR_HEART_STROKE);

export type HeartStrokeAction =
  | ReturnType<typeof changeProductType>
  | ReturnType<typeof changePlanType>
  | ReturnType<typeof changePolicyOption>
  | ReturnType<typeof changePremium>
  | ReturnType<typeof changeCalculatedPremium>
  | ReturnType<typeof changeCWA>
  | ReturnType<typeof changeSection125>
  | ReturnType<typeof changeReplacement>
  | ReturnType<typeof changePolicyNumber>
  | ReturnType<typeof changeRiderCodes>
  | ReturnType<typeof changeRiderAmt>
  | ReturnType<typeof changeOverrideBillForm>
  | ReturnType<typeof getHeartStrokeProducts>
  | ReturnType<typeof changeHeartStrokeProducts>
  | ReturnType<typeof changeBaseUnits>
  | ReturnType<typeof changeRiderInformation>
  | ReturnType<typeof clearHeartStroke>;

type HeartStrokeState = HeartStroke;

export default (state: HeartStrokeState, action: HeartStrokeAction): HeartStrokeState => {
  switch (action.type) {
    case HeartStrokeActionType.CHANGE_PRODUCT_TYPE:
      return matchHeartStrokeByType<HeartStroke>({
        Ordinary: O.changeProductType(action.payload.productType),
        Group: G.changeProductType(action.payload.productType),
        NewYork: NY.changeProductType(action.payload.productType),
      })(state);
    case HeartStrokeActionType.CHANGE_PLAN_TYPE:
      return matchHeartStrokeByType<HeartStroke>({
        Ordinary: O.changePlanType(action.payload.planType),
        Group: G.changePlanType(action.payload.planType),
        NewYork: NY.changePlanType(action.payload.planType),
      })(state);
    case HeartStrokeActionType.CHANGE_PREMIUM:
      return matchHeartStrokeByType<HeartStroke>({
        Ordinary: O.changePremium(action.payload.premium),
        Group: G.changePremium(action.payload.premium),
        NewYork: NY.changePremium(action.payload.premium),
      })(state);
    case HeartStrokeActionType.CHANGE_CALCULATED_PREMIUM:
      return matchHeartStrokeByType<HeartStroke>({
        Ordinary: O.changeCalculatedPremium(action.payload.calculatedPremium),
        Group: G.changeCalculatedPremium(action.payload.calculatedPremium),
        NewYork: NY.changeCalculatedPremium(action.payload.calculatedPremium),
      })(state);
    case HeartStrokeActionType.CHANGE_CWA:
      return matchHeartStrokeByType<HeartStroke>({
        Ordinary: O.changeCWA(action.payload.cwa),
        Group: G.changeCWA(action.payload.cwa),
        NewYork: NY.changeCWA(action.payload.cwa),
      })(state);
    case HeartStrokeActionType.CHANGE_SECTION125:
      return matchHeartStrokeByType<HeartStroke>({
        Ordinary: O.changeSection125(action.payload.section125),
        Group: G.changeSection125(action.payload.section125),
        NewYork: NY.changeSection125(action.payload.section125),
      })(state);
    case HeartStrokeActionType.CHANGE_REPLACEMENT:
      return matchHeartStrokeByType<HeartStroke>({
        Ordinary: O.changeReplacement(action.payload.replacement),
        Group: G.changeReplacement(action.payload.replacement),
        NewYork: NY.changeReplacement(action.payload.replacement),
      })(state);
    case HeartStrokeActionType.CHANGE_POLICY_NUMBER:
      return matchHeartStrokeByType<HeartStroke>({
        Ordinary: O.changePolicyNumber(NonEmptyString.create(action.payload.policyNumber)),
        Group: G.changePolicyNumber(NonEmptyString.create(action.payload.policyNumber)),
        NewYork: NY.changePolicyNumber(NonEmptyString.create(action.payload.policyNumber)),
      })(state);
    case HeartStrokeActionType.CHANGE_RIDER_CODES:
      return matchHeartStrokeByType<HeartStroke>({
        Group: G.changeRiderCodes(action.payload.riderCode, action.payload.index),
        NewYork: NY.changeRiderCodes(action.payload.riderCode, action.payload.index),
        _: () => state,
      })(state);
    case HeartStrokeActionType.CHANGE_RIDER_AMT:
      return matchHeartStrokeByType<HeartStroke>({
        Group: G.changeRiderAmount(action.payload.riderAmt, action.payload.index),
        NewYork: NY.changeRiderAmount(action.payload.riderAmt, action.payload.index),
        _: () => state,
      })(state);
    case HeartStrokeActionType.CHANGE_OVERRIDE_BILL_FORM:
      return matchHeartStrokeByType<HeartStroke>({
        Ordinary: O.changeOverrideBillForm(action.payload.overrideBillForm),
        Group: G.changeOverrideBillForm(action.payload.overrideBillForm),
        NewYork: NY.changeOverrideBillForm(action.payload.overrideBillForm),
      })(state);
    case HeartStrokeActionType.GET_HEART_STROKE_PRODUCTS:
      return matchHeartStrokeByType<HeartStroke>({
        Group: G.changeProductList(new Loading()),
        Ordinary: O.changeProductList(new Loading()),
        NewYork: NY.changeProductList(new Loading()),
      })(state);
    case HeartStrokeActionType.CHANGE_HEART_STROKE_PRODUCTS:
      return matchHeartStrokeByType<HeartStroke>({
        Group: G.changeProductList(action.payload.products),
        Ordinary: O.changeProductList(action.payload.products),
        NewYork: NY.changeProductList(action.payload.products),
      })(state);
    case HeartStrokeActionType.CHANGE_BASE_UNITS:
      return matchHeartStrokeByType<HeartStroke>({
        Ordinary: O.changeBaseUnits(action.payload.baseUnits),
        Group: G.changeBaseUnits(action.payload.baseUnits),
        NewYork: NY.changeBaseUnits(action.payload.baseUnits),
      })(state);
    case HeartStrokeActionType.CHANGE_RIDER_INFORMATION:
      return matchHeartStrokeByType<HeartStroke>({
        Ordinary: O.changeRiderInformation(action.payload.amount, action.payload.code, action.payload.index),
        _: () => state,
      })(state);
    case HeartStrokeActionType.CLEAR_HEART_STROKE:
      if (state.key === 'InvalidGroupHeartStroke' || state.key === 'ValidGroupHeartStroke') {
        return G.EmptyGroupHeartStroke;
      } else if (state.key === 'InvalidNewYorkHeartStroke' || state.key === 'ValidNewYorkHeartStroke') {
        return NY.EmptyNewYorkHeartStroke;
      } else if (state.key === 'InvalidOrdinaryHeartStroke' || state.key === 'ValidOrdinaryHeartStroke') {
        return O.EmptyOrdinaryHeartStroke;
      }

      return state;
    default:
      return state;
  }
};
